import { sendBizEvent } from '@kitopi-com/payments';
import { useEffect, useMemo, useRef } from 'react';
import { API_EVENTS_KEY } from '@/constants/config';
import { VITE_APP_ENV } from '@/constants/constants';
import { useCheckoutContext } from '@/context/CheckoutContext/CheckoutContext';
import { useBrand } from '@/hooks/useBrand';
import { useBrowserUserId } from '@/hooks/useBrowserUserId';
import useValidateQueryParams from '@/hooks/useValidateQueryParams';
import type { Bill as BillDetailsType } from '@/interfaces/Bill';
import { setOutletContext, setSplitBillData } from '@/screens/Checkout/monitoring';
import { trackEvent } from '@/utils/gtmButtonClicked';
import { useOutletKitchen } from '../LinkToMenu/useOutletKitchen';

interface CheckoutMonitoringParams {
  bill?: BillDetailsType;
  isBillError?: boolean;
  isLoading: boolean;
  isSplitExist: boolean;
  yourShare: number | null;
  sessionId: string;
}

/**
 * The checkout's observability side-channel — no render output. Keeps the
 * monitoring context (outlet, split bill data) current, reports checkout
 * init failures, and sends the one-time payment-view events.
 */
export const useCheckoutMonitoring = ({
  bill,
  isBillError,
  isLoading,
  isSplitExist,
  yourShare,
  sessionId,
}: CheckoutMonitoringParams) => {
  const { queryParams } = useValidateQueryParams();
  const { outletId, brandId } = queryParams();
  const { data: brandData } = useBrand();
  const browserUserId = useBrowserUserId();
  const { currency, country } = useOutletKitchen(outletId);
  const {
    state: { numberOfSplits, payingFor, splitType, baseToPay },
  } = useCheckoutContext();
  const isItemsSplitActive = splitType === 'SPLIT_BY_ITEMS';

  const mountedRef = useRef(false);
  const checkoutInitFailedSentRef = useRef(false);

  const appEnv = VITE_APP_ENV === 'local' || !VITE_APP_ENV ? 'dev' : VITE_APP_ENV;
  const bizEventConfig = useMemo(
    () => ({ appEnv, paymentSource: 'KITOPAY_WEB' as const, sessionId, apiKey: API_EVENTS_KEY }),
    [appEnv, sessionId]
  );

  useEffect(() => {
    if (isBillError && !checkoutInitFailedSentRef.current) {
      checkoutInitFailedSentRef.current = true;
      sendBizEvent(bizEventConfig, { name: 'checkoutInitFailed' });
    }
  }, [isBillError, bizEventConfig]);

  useEffect(() => {
    if (brandId && outletId && country) {
      setOutletContext({ brandId, outletId, country, currency: currency || undefined });
    }
  }, [brandId, outletId, country, currency]);

  useEffect(() => {
    if (bill) {
      setSplitBillData({
        splitBillEnabled: numberOfSplits > 0 || isItemsSplitActive,
        numberOfSplits,
        payingFor,
        orderValue: bill.baseTotal,
        serviceFeeAmount: bill.serviceFee || 0,
        brandName: brandData?.name || '',
        orderId: bill.orderId || bill.orderNumber,
      });
    }
  }, [bill, numberOfSplits, payingFor, brandData?.name, isItemsSplitActive]);

  useEffect(() => {
    if (mountedRef.current || isLoading || !bill || !outletId) return;
    mountedRef.current = true;
    trackEvent('web_kitopay_payment_view', {
      split_created: isSplitExist ? 'yes' : 'no',
      outlet_id: outletId,
      order_value: bill?.baseTotal.toString(),
      service_fee_shown: 'yes',
      browser_user_id: browserUserId,
      left_to_pay: baseToPay.toString(),
      share: yourShare?.toString() || '0',
    });
    sendBizEvent(bizEventConfig, { name: 'checkoutScreenViewed' });
  }, [
    browserUserId,
    isLoading,
    isSplitExist,
    outletId,
    bill,
    baseToPay,
    yourShare,
    bizEventConfig,
  ]);
};
